import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { authService } from '../../../services';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useClientUIContext } from './context/ClientByIdUIContext';
import EmailSection from './forms/sections/EmailSection';
import { EmailSVG } from '../../../components/svg/EmailSVG';
import { AddUserSVG } from '../../../components/svg/AddUserSVG';
import types from '../../../constants/emails/types';
import EmailModalContent from '../../../components/modal/content/EmailModalContent';
import GeneralModal from '../../../components/modal/GeneralModal';
import { getColor } from '../../../helpers/getColor';
import CustomAlert from '../../../components/Alert';
import { palette } from '../../../styles/theme';
import { getCoreLocale } from '../../../helpers';

export default function ClientEmailById () {
    const { t } = useTranslation();
    const { id } = useParams();
    const clientId = parseInt(id);
    const clientUIContext = useClientUIContext();
    const clientUIProps = useMemo(() => {
        return {
            isLoading: clientUIContext?.isLoading,
            setIsLoading: clientUIContext?.setIsLoading,
            clientGeneral: clientUIContext?.clientGeneral,
            setClientGeneral: clientUIContext?.setClientGeneral,
            clientEmail: clientUIContext?.clientEmail,
            setClientEmail: clientUIContext?.setClientEmail,
            clientTabData: clientUIContext.clientTabData,
            setClientTabData: clientUIContext.setClientTabData,
        };
    }, [clientUIContext]);

    const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
    const [emailFieldValue, setEmailFieldValue] = useState('');

    // Alert states

    const [openAlert, setOpenAlert] = useState(false);
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: ''
    });

    const [sessionUser, setSessionUser] = useState(null);
    const { clientTabData, setIsLoading, setClientEmail } = clientUIProps;

    const fetchEmailsById = useCallback(() => {
        setIsLoading(true);
        setClientEmail([]);
        try {
            if (!clientTabData || !clientTabData.person) {
                return;
            }
            const emailsResponse = clientTabData.person.emails || [];
            if (emailsResponse.length) {
                const parsedEmails = emailsResponse.filter(
                    email => email.id_email_type === types.principal.id || email.type?.id === types.principal.id
                );
                setClientEmail(parsedEmails);
            }
            else throw new Error('No emails found');
        }
        catch (error) {
            setAlert({
                isOpen: true,
                severity: 'error',
                title: t('errors.generalTitle'),
                description: t('errors.generalDescription'),
                className: 'clientsMC_Alert--error'
            });
            setOpenAlert(true);
        }
        finally {
            setIsLoading(false);
        }
    }, [clientTabData, setIsLoading, setClientEmail, t]);

    const checkEmailField = () => {
        if (emailFieldValue === '' || !emailFieldValue) return true;
        if (!/^[+a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailFieldValue)) return true;
        if (emailFieldValue === clientUIProps?.clientTabData?.person?.email.description) return true;
        return false;
    };

    const handleModifyMail = async () => {
        clientUIProps.setIsLoading(true);

        const idPerson =
            clientUIProps.clientEmail?.[0]?.metadata?.id_person ||
            clientUIProps.clientEmail?.[0]?.id_person ||
            clientUIProps.clientTabData?.person?.id ||
            clientUIProps.clientTabData?.person?.id_person ||
            clientUIProps.clientTabData?.id_person ||
            clientId;

        const data = {
            email: emailFieldValue,
            idUser: clientId,
            idPerson,
            idUserBO: sessionUser?.id,
            metadata: {
                locale: getCoreLocale()
            }
        };

        try {
            const response = await authService.verifyNewEmail(data);

            if (response.error) throw new Error(response.error);
            if (response) {
                setAlert({
                    isOpen: true,
                    severity: 'info',
                    title: t('email.alert.info.title'),
                    description: t('email.alert.info.description'),
                    className: 'clientsMC_Alert--info'
                });
            }
        }
        catch (error) {
            console.error('Error modifying email:', error);
            setAlert({
                isOpen: true,
                severity: 'warning',
                title: `${t('email.alert.warning.title1')} ${emailFieldValue} ${t('email.alert.warning.title2')}`,
                description: t('email.alert.warning.description'),
                className: 'clientsMC_Alert--warning-light'
            });
        }
        finally {
            clientUIProps.setIsLoading(false);
            setIsModifyModalOpen(!isModifyModalOpen);
        }
    };

    useEffect(() => {
        fetchEmailsById();
    }, [fetchEmailsById]);

    useEffect(() => {
        const storageSessionUser = localStorage.getItem('sessionUser');
        setSessionUser(storageSessionUser ? JSON.parse(storageSessionUser) : null);
    }, [clientId]);

    return (
        <>
            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />

            <Box style={{ backgroundColor: '#FFF', boxShadow: '0px 0px 30px 0px rgba(82, 63, 105, 0.06)' }} width={'100%'} marginTop={'1rem'} padding={'1rem'} >
                <Box gap={'1rem'}>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <Box display='flex' gap={'8px'}>
                            {<EmailSVG customStroke={getColor('primary', 700)} />}
                            <Typography variant='h5'>{t('email.title')}</Typography>
                        </Box>
                        <Button
                            variant='contained'
                            className='buttonAction'
                            style={{
                                boxShadow: 'none',
                            }}
                            onClick={() => setIsModifyModalOpen(!isModifyModalOpen)}
                            disabled={clientUIProps.isLoading || alert.isOpen}
                        >
                            {clientUIProps.isLoading ? <CircularProgress size={24} color="inherit" /> : t('email.actions')}
                        </Button>
                    </Box>
                    <GeneralModal
                        mainButton={{
                            isDisabled: clientUIProps.isLoading || checkEmailField(),
                            onMainButtonClick: handleModifyMail,
                            title: 'buttons.modify',
                            sx: {
                                backgroundColor: `${palette.primary[500]} !important`,
                                color: 'white !important',
                                borderRadius: '3.85rem !important',
                                padding: '0.77rem 1.85rem !important',
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '1.08rem',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: `${palette.primary[600]} !important`,
                                    boxShadow: 'none',
                                },
                                '&:disabled': {
                                    backgroundColor: `${palette.disabled[200]} !important`,
                                    color: `${palette.disabled[500]} !important`,
                                }
                            }
                        }}
                        title={'email.actions'}
                        width='32.92rem'
                        height='21.54rem'
                        borderRadius='0.62rem'
                        padding='1.85rem 1.23rem 1.23rem 1.23rem'
                        headerDirection='row'
                        contentStyle={{
                            marginTop: '2.77rem',
                            marginBottom: '0rem',
                            alignItems: 'flex-start',
                            overflow: 'auto'
                        }}
                        actionsStyle={{
                            padding: '0rem',
                            marginTop: '1.54rem'
                        }}
                        isOpen={isModifyModalOpen}
                        onClose={() => setIsModifyModalOpen(!isModifyModalOpen)}
                        icon={<AddUserSVG customStroke={palette.primary[700]} size='2.46rem' />}
                        secondaryButton={{
                            onSecondaryButtonClick: () => setIsModifyModalOpen(!isModifyModalOpen),
                            title: 'buttons.cancel',
                            sx: {
                                color: `${palette.secondary.main} !important`,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '1.08rem',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                }
                            }
                        }}
                        UIProps={clientUIProps}
                    >
                        <EmailModalContent emailField={{ emailFieldValue, setEmailFieldValue }} />
                    </GeneralModal>
                    <EmailSection emails={clientUIProps.clientEmail} isLoading={clientUIProps.isLoading} />
                </Box>
            </Box>
        </>

    );
}
